import type { AgentRunRecord, AiEvent, ChangeSetInfo, ChatMessage, CommandView, ToolActivity } from "@racler/protocol";

/**
 * Pure state transitions for the chat view. The server is the source of truth for what is persisted; this
 * mirrors the same offset-based de-duplication so streamed events and a later reload always agree.
 */
export interface ChatState {
  sessionId: string | null;
  messages: ChatMessage[];
  /** Run currently streaming in this chat, if any. */
  activeRunId: string | null;
  /** M10A: Active or recent durable run state. */
  activeRun?: AgentRunRecord | null;
  /** Proposals (ChangeSets) of this chat, oldest first. Metadata only; contents are fetched for review. */
  changeSets: ChangeSetInfo[];
  /** Commands the model requested in this chat (Agent mode), oldest first. */
  commands: CommandView[];
}

export const emptyChat: ChatState = {
  sessionId: null,
  messages: [],
  activeRunId: null,
  activeRun: null,
  changeSets: [],
  commands: [],
};

/** Inserts or replaces a ChangeSet by id (server `changeset.status` events and approve/reject replies). */
export function upsertChangeSet(state: ChatState, cs: ChangeSetInfo): ChatState {
  if (state.sessionId !== cs.sessionId) return state;
  const i = state.changeSets.findIndex((c) => c.id === cs.id);
  // Never move a ChangeSet backwards: statuses only advance, so a late event cannot undo a newer state.
  if (i >= 0 && rank(state.changeSets[i]!.status) > rank(cs.status)) return state;
  const changeSets = i >= 0 ? state.changeSets.map((c, k) => (k === i ? cs : c)) : [...state.changeSets, cs];
  let activeRun = state.activeRun;
  if (cs.status === "pending_approval" && activeRun && activeRun.id === cs.runId) {
    activeRun = { ...activeRun, status: "awaiting_approval", stage: "awaiting_approval" };
  }
  return { ...state, changeSets, activeRun };
}

const ORDER = [
  "draft",
  "pending_approval",
  "approved",
  "applying",
  "rejected",
  "discarded",
  "applied",
  "conflicted",
  "failed",
];
const rank = (s: ChangeSetInfo["status"]) => ORDER.indexOf(s);

const patchRun = (state: ChatState, runId: string, fn: (m: ChatMessage) => ChatMessage): ChatState => ({
  ...state,
  messages: state.messages.map((m) => (m.role === "assistant" && m.runId === runId ? fn(m) : m)),
});

function ensureAssistantRunMessage(state: ChatState, runId: string): ChatState {
  if (state.messages.some((m) => m.role === "assistant" && m.runId === runId)) {
    return state;
  }
  const synthetic: ChatMessage = {
    id: runId,
    role: "assistant",
    content: "",
    status: "running",
    runId,
    activity: [],
    provider: state.activeRun?.provider ?? null,
    model: state.activeRun?.model ?? null,
    stopReason: null,
    error: null,
    usage: null,
    createdAt: new Date().toISOString(),
  };
  return {
    ...state,
    messages: [...state.messages, synthetic],
    activeRunId: runId,
  };
}

export function applyAiEvent(state: ChatState, ev: AiEvent): ChatState {
  if (ev.type === "agent/ai.sync") return state;
  const { runId, sessionId } = ev.payload;
  if (state.sessionId !== sessionId) return state;
  state = ensureAssistantRunMessage(state, runId);
  switch (ev.type) {
    case "agent/response.started": {
      const activeRun = state.activeRun && state.activeRun.id === runId
        ? { ...state.activeRun, status: "running" as const, stage: "thinking" }
        : state.activeRun;
      return { ...state, activeRunId: runId, activeRun };
    }
    case "agent/response.delta": {
      const { offset, text } = ev.payload;
      const patched = patchRun(state, runId, (m) => {
        if (m.status !== "running" || offset + text.length <= m.content.length) return m;
        const fresh = offset < m.content.length ? text.slice(m.content.length - offset) : text;
        return { ...m, content: m.content + fresh };
      });
      const activeRun = patched.activeRun && patched.activeRun.id === runId
        ? {
            ...patched.activeRun,
            stage: patched.activeRun.stage === "started" ? "thinking" : patched.activeRun.stage,
          }
        : patched.activeRun;
      return { ...patched, activeRunId: runId, activeRun };
    }
    case "agent/tool.started": {
      const { toolCallId, name, args, iteration } = ev.payload;
      const patched = patchRun(state, runId, (m) =>
        m.activity.some((a) => a.toolCallId === toolCallId)
          ? m
          : { ...m, activity: [...m.activity, { toolCallId, name, args, iteration, ok: null }] },
      );
      const activeRun = patched.activeRun && patched.activeRun.id === runId
        ? { ...patched.activeRun, stage: name }
        : patched.activeRun;
      return { ...patched, activeRun };
    }
    case "agent/tool.completed": {
      const { toolCallId, ok, errorCode, summary, durationMs } = ev.payload;
      const done = (a: ToolActivity): ToolActivity => ({
        ...a,
        ok,
        summary,
        durationMs,
        ...(errorCode ? { errorCode } : {}),
      });
      const patched = patchRun(state, runId, (m) => ({
        ...m,
        activity: m.activity.map((a) => (a.toolCallId === toolCallId ? done(a) : a)),
      }));
      const activeRun = patched.activeRun && patched.activeRun.id === runId
        ? { ...patched.activeRun, stage: "thinking" }
        : patched.activeRun;
      return { ...patched, activeRun };
    }
    case "agent/response.completed": {
      const next = patchRun(state, runId, (m) => ({
        ...m,
        status: ev.payload.stopReason === "cancelled" ? "cancelled" : "complete",
        stopReason: ev.payload.stopReason,
        usage: ev.payload.usage,
      }));
      const isPaused = ev.payload.stopReason === "awaiting_approval";
      const activeRun = next.activeRun && next.activeRun.id === runId
        ? {
            ...next.activeRun,
            status: isPaused ? ("awaiting_approval" as const) : ev.payload.stopReason === "cancelled" ? ("cancelled" as const) : ("completed" as const),
            stage: isPaused ? "awaiting_approval" : ev.payload.stopReason === "cancelled" ? "cancelled" : "completed",
            finishedAt: isPaused ? null : new Date().toISOString(),
          }
        : next.activeRun;
      return {
        ...next,
        activeRunId: isPaused ? null : state.activeRunId === runId ? null : state.activeRunId,
        activeRun,
      };
    }
    case "agent/response.error": {
      const next = patchRun(state, runId, (m) => ({ ...m, status: "error", error: ev.payload.message }));
      const isInterrupted = ev.payload.code === "interrupted";
      const activeRun = next.activeRun && next.activeRun.id === runId
        ? {
            ...next.activeRun,
            status: isInterrupted ? ("interrupted" as const) : ("failed" as const),
            stage: isInterrupted ? "interrupted" : "failed",
            error: ev.payload.message,
            finishedAt: new Date().toISOString(),
          }
        : next.activeRun;
      return {
        ...next,
        activeRunId: state.activeRunId === runId ? null : state.activeRunId,
        activeRun,
      };
    }
    default:
      return state;
  }
}

/** Adds the optimistic/confirmed user + assistant messages returned by chat.send. */
export function withSentMessages(
  state: ChatState,
  sent: { sessionId: string; runId: string; userMessage: ChatMessage; assistantMessage: ChatMessage },
): ChatState {
  const base = state.sessionId === sent.sessionId ? state.messages : [];
  return {
    sessionId: sent.sessionId,
    messages: [...base, sent.userMessage, sent.assistantMessage],
    activeRunId: sent.runId,
    activeRun: {
      id: sent.runId,
      userId: "",
      machineId: "",
      projectId: "",
      chatSessionId: sent.sessionId,
      messageId: sent.assistantMessage.id,
      mode: "ask",
      provider: sent.assistantMessage.provider ?? "",
      model: sent.assistantMessage.model ?? "",
      status: "running",
      stage: "started",
      error: null,
      createdAt: new Date().toISOString(),
      startedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      finishedAt: null,
      events: [],
    },
    changeSets: state.sessionId === sent.sessionId ? state.changeSets : [],
    commands: state.sessionId === sent.sessionId ? state.commands : [],
  };
}

/**
 * chat.send replies only after the agent accepted the run, so the first stream events can reach the browser
 * BEFORE the reply that creates the assistant message. They are buffered while the send is in flight and replayed
 * here, in order, on top of the new messages (offset-based de-duplication keeps this idempotent).
 */
export function withSentAndBuffered(
  state: ChatState,
  sent: Parameters<typeof withSentMessages>[1],
  buffered: readonly AiEvent[],
): ChatState {
  return buffered.reduce(applyAiEvent, withSentMessages(state, sent));
}

/** Compact "1.2k in · 340 out" usage label; cost is shown only when the provider reported it. */
export function usageLabel(u: ChatMessage["usage"]): string {
  if (!u) return "";
  const n = (x: number) => (x >= 1000 ? `${(x / 1000).toFixed(1)}k` : String(x));
  const parts = [`${n(u.inputTokens)} in`, `${n(u.outputTokens)} out`];
  if (u.cachedTokens) parts.push(`${n(u.cachedTokens)} cached`);
  if (u.costUsd !== undefined)
    parts.push(`$${u.costUsd < 0.01 ? u.costUsd.toFixed(4) : u.costUsd.toFixed(2)}`);
  return parts.join(" · ");
}

const CMD_ORDER = [
  "pending_approval",
  "running",
  "denied",
  "expired",
  "completed",
  "failed",
  "timed_out",
  "cancelled",
];

/** Inserts or replaces a command by id; statuses only advance, so a late event cannot undo a newer state. */
export function upsertCommand(state: ChatState, c: CommandView): ChatState {
  if (state.sessionId !== c.sessionId) return state;
  const i = state.commands.findIndex((x) => x.id === c.id);
  if (i >= 0 && CMD_ORDER.indexOf(state.commands[i]!.status) > CMD_ORDER.indexOf(c.status)) return state;
  const commands = i >= 0 ? state.commands.map((x, k) => (k === i ? c : x)) : [...state.commands, c];
  let activeRun = state.activeRun;
  if (c.status === "pending_approval" && activeRun && activeRun.id === c.runId) {
    activeRun = { ...activeRun, status: "awaiting_approval", stage: "awaiting_approval" };
  }
  return { ...state, commands, activeRun };
}

/** A run is paused (waiting for a human) when it ended with `awaiting_approval`. */
export const isPaused = (m: ChatMessage): boolean => m.stopReason === "awaiting_approval";
